import {
  CheckIcon,
  ChevronDown,
  SlidersHorizontal,
  XCircle,
  XIcon,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { EnhancedOption } from "./OptionSelectorMemo";

import ButtonWithIcon from "@/components/ui/ButtonWithIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { customToast } from "@/utils/custom-toast";

type SimplifiedOptionSelectorProps<T> = {
  statusFor: string;
  activeId: string;
  optionList: EnhancedOption<T>[];
  setIsOption: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      statusFor: string;
    }>
  >;
  isOptionAdditional?: () => void;
  onValueChange: (value: string | string[]) => Promise<void>;
  disabled?: boolean;
  className?: string;
  showIcon?: boolean;
  placeholder?: string;
  isMultiple?: boolean;
  config?: {
    isRequired?: boolean;
    isFinal?: boolean;
  };
  // Pre-computed props to prevent recalculation
  selectedOptionWithStyles?: EnhancedOption<T> | EnhancedOption<T>[] | null;
  sortedOptionsForDropdown?: EnhancedOption<T>[];
};

export const SimplifiedOptionSelector = <T,>({
  activeId,
  statusFor,
  optionList,
  setIsOption,
  onValueChange,
  disabled = false,
  showIcon,
  className,
  placeholder,
  isOptionAdditional,
  isMultiple = false,
  config = { isRequired: true },
  selectedOptionWithStyles,
  sortedOptionsForDropdown,
}: SimplifiedOptionSelectorProps<T>): React.JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const [searchedText, setSearchedText] = React.useState("");
  const [isUpdating, setIsUpdating] = React.useState(false);

  // Use activeId to determine selected values
  const selectedValues = React.useMemo(() => {
    // eslint-disable-next-line no-nested-ternary
    return Array.isArray(activeId) ? activeId : activeId ? [activeId] : [];
  }, [activeId]);

  // Filter options based on search
  const filterOptions = React.useMemo(() => {
    const optionsToFilter = sortedOptionsForDropdown || optionList;

    return optionsToFilter.filter((option) =>
      option?.name?.toLowerCase()?.includes(searchedText?.toLowerCase()),
    );
  }, [sortedOptionsForDropdown, optionList, searchedText]);

  const handleOptionChange = React.useCallback(
    async (currentValue: string): Promise<void> => {
      if (currentValue === "" || config.isFinal || isUpdating) {
        if (config.isFinal) {
          customToast.warn("Cannot Update Finalized data.");
        }

        return;
      }

      setIsUpdating(true);

      try {
        if (isMultiple) {
          const isSelected = selectedValues.includes(currentValue);
          const newSelectedValues = isSelected
            ? selectedValues.filter((val) => val !== currentValue)
            : [...selectedValues, currentValue];

          await onValueChange(newSelectedValues);
        } else {
          const isSelected = selectedValues.includes(currentValue);

          if (isSelected && config.isRequired) {
            toast.warning("Option already selected");

            return;
          } else if (isSelected && !config.isRequired) {
            await onValueChange("null");
          } else {
            await onValueChange(currentValue);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating option:", error);
      } finally {
        setIsUpdating(false);
      }
    },
    [selectedValues, config, isMultiple, onValueChange, isUpdating],
  );

  const renderContent = React.useCallback((): JSX.Element => {
    if (selectedValues?.length === 0) {
      return (
        <>
          <span className='ml-2'>{placeholder || "Select Option"}</span>
          {showIcon && <ChevronDown size={18} className='mr-2' />}
        </>
      );
    }

    if (
      selectedValues?.length > 0 &&
      isMultiple &&
      Array.isArray(selectedOptionWithStyles)
    ) {
      return (
        <div className='flex items-center justify-between w-full'>
          <div className='flex flex-wrap items-center'>
            {selectedOptionWithStyles.map((option) => (
              <Badge
                key={option.id}
                className='m-1 transition duration-300 ease-in-out delay-150 hover:scale-110'
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleOptionChange(option.id || "");
                }}
                style={{
                  backgroundColor: option.computedBackgroundColor,
                  opacity: option.computedTextOpacity,
                  color: option.computedTextColor,
                  fontWeight: 400,
                }}
              >
                {option?.name?.substring(0, 25)}
                <XCircle className='w-4 h-4 ml-2 cursor-pointer' />
              </Badge>
            ))}
          </div>
          <div className='flex items-center justify-between'>
            <XIcon
              className='h-4 mx-2 cursor-pointer text-muted-foreground'
              onClick={async (event) => {
                event.stopPropagation();
                await onValueChange([]);
              }}
            />
            <ChevronDown className='h-4 mx-2 cursor-pointer text-muted-foreground' />
          </div>
        </div>
      );
    }

    // Single selection
    const singleOption = Array.isArray(selectedOptionWithStyles)
      ? selectedOptionWithStyles[0]
      : selectedOptionWithStyles;

    return (
      <span
        style={{
          opacity: singleOption?.computedTextOpacity || 1,
          color: singleOption?.computedTextColor || "#ffffff",
          fontWeight: 400,
        }}
        className='ml-2 truncate'
      >
        {singleOption?.name?.substring(0, 25)}
      </span>
    );
  }, [
    selectedValues,
    placeholder,
    showIcon,
    isMultiple,
    selectedOptionWithStyles,
    handleOptionChange,
    onValueChange,
  ]);

  // Get button background color
  const buttonBackgroundColor = React.useMemo(() => {
    if (isMultiple || selectedValues.length === 0) {
      return "transparent";
    }

    const singleOption = Array.isArray(selectedOptionWithStyles)
      ? selectedOptionWithStyles[0]
      : selectedOptionWithStyles;

    return singleOption?.computedBackgroundColor || "transparent";
  }, [isMultiple, selectedValues.length, selectedOptionWithStyles]);

  return (
    <div
      className={`transition-opacity duration-200 ${isUpdating ? "opacity-75" : "opacity-100"}`}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            role='combobox'
            aria-expanded={open}
            className={cn(
              selectedValues.length > 0
                ? "text-white"
                : "text-gray-400 hover:text-gray-300",
              "min-w-[200px] flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit active:scale-100",
              (disabled || isUpdating) && "pointer-events-none opacity-50",
              className,
            )}
            variant={"outline"}
            style={{
              backgroundColor: buttonBackgroundColor,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {renderContent()}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className={cn(
            "p-1",
            "popover-content-width-full max-h-64 overflow-y-auto scrollbar-thin",
          )}
        >
          <Command>
            <CommandInput
              placeholder='Search Status...'
              className='h-8'
              value={searchedText}
              onValueChange={setSearchedText}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup onClick={(e) => e.stopPropagation()}>
              {filterOptions.map((option) => {
                const isSelected = selectedValues.includes(option?.id || "");

                //  this is for css injection
                const escapedValue = CSS.escape(option?.name ?? "");

                return (
                  <CommandItem
                    key={option.id}
                    value={escapedValue}
                    onSelect={async () => {
                      await handleOptionChange(option?.id || "");
                      setOpen(false);
                    }}
                    className='py-2 cursor-pointer'
                    style={{
                      backgroundColor: option.computedBackgroundColor,
                      marginTop: "4px",
                      color: "white",
                    }}
                  >
                    <span
                      style={{
                        opacity: option.computedTextOpacity,
                        color: option.computedTextColor,
                        fontWeight: 400,
                      }}
                      className='line-clamp-1'
                    >
                      {option?.name || ""}
                    </span>
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <ButtonWithIcon
              icon={<SlidersHorizontal size={16} className='mr-2' />}
              variant={"secondary"}
              className='m-1 text-xs'
              onClick={(e) => {
                e.stopPropagation();
                setIsOption((prev) => ({
                  statusFor,
                  isOpen: !prev.isOpen,
                }));
                isOptionAdditional?.();
              }}
            >
              Manage Options
            </ButtonWithIcon>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

SimplifiedOptionSelector.displayName = "SimplifiedOptionSelector";
